import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { Grid } from '~/components/layout'
import { Logo } from '~/components/logo'
import { useModal } from '~/components/modal'
import { Meta } from '~/components/typography'
import { Button } from '~/components/button'
import { routes } from '~/config/routes'
import { useSignOutUser } from '~/hooks/use-sign-out-user'
import { accessToken } from '~/utils/access-token'

import { Order } from './components/order'
import { OrderLoader } from './components/order-loader'
import { TicketOrder } from './components/ticket-order'
import { useCurrentUserOrdersQuery } from './graphql/current-user-orders'
import { useCurrentUserTicketsQuery } from './graphql/current-user-tickets'

const ModalTaxReport = dynamic(
  () => import('./components/modal-tax-report')
)

const tabClass = twMerge(
  'py-2 px-1 text-lg font-normal text-slateGray',
  'data-[selected]:text-dark data-[selected]:border-b-2 data-[selected]:border-primary',
  'outline-none'
)

const TAB_NAMES = ['donations', 'tickets'] as const
type TabName = typeof TAB_NAMES[number]

export const DonorPortal: NextPage = () => {
  const router = useRouter()
  const signOutUser = useSignOutUser('donor-portal')
  const modalTaxReport = useModal()

  const bearerAuth = {
    skip: !accessToken.get('donor-portal'),
    context: {
      headers: {
        Authorization: `Bearer ${accessToken.get('donor-portal')}`,
      },
    },
    ssr: false,
    onError: () => {
      signOutUser(routes.donorPortal.signIn)
    },
  }

  const { data: ordersData } = useCurrentUserOrdersQuery(bearerAuth)
  const { data: ticketsData } = useCurrentUserTicketsQuery(bearerAuth)

  const hasOrders = ordersData && ordersData.currentUserOrders.length > 0
  const hasTickets = ticketsData && ticketsData.currentUserTickets.length > 0
  const visibleTabs: TabName[] = hasTickets ? [...TAB_NAMES] : ['donations']
  const activeTab = (router.query.tab as TabName) ?? 'donations'
  const normalizedTab = visibleTabs.includes(activeTab) ? activeTab : 'donations'
  const tabIndex = visibleTabs.indexOf(normalizedTab)

  const handleTabChange = (index: number) => {
    const tab = visibleTabs[index]
    router.replace(
      { pathname: routes.donorPortal.home, query: { tab } },
      undefined,
      { shallow: true },
    )
  }

  return (
    <>
      <header className={twMerge(
        "sticky z-[99] top-0 left-0 w-full h-[4.5rem] bg-white",
        "flex items-center justify-between px-4 lg:px-8 mb-6 sm:mb-10",
        "border-b-[1px] border-whiteFog"
      )}>
        <Logo />
        <Meta
          as="button"
          type="button"
          onClick={() => signOutUser(routes.donorPortal.signIn)}
        >
          Sign Out
        </Meta>
      </header>

      <div className="max-w-[864px] mx-auto my-0 px-4 lg:px-8">
        <Tabs index={tabIndex} onChange={handleTabChange}>
          <div className="flex justify-between items-end mb-6 flex-wrap gap-4 border-b border-whiteFog">
            <TabList className="flex gap-6">
              <Tab className={tabClass}>Donations</Tab>
              {hasTickets && (
                <Tab className={tabClass}>Tickets</Tab>
              )}
            </TabList>
            {hasOrders && (
              <div className="pb-2">
                <Button size="small" onClick={modalTaxReport.show} variant='outline'>
                  Tax Report
                </Button>
              </div>
            )}
          </div>

          <TabPanels>
            <TabPanel>
              <Grid gridRowGap={32} paddingBottom={100}>
                {ordersData ? (
                  hasOrders ? (
                    ordersData.currentUserOrders.map(order => (
                      <Order key={order.id} order={order} />
                    ))
                  ) : (
                    <div className="text-center py-16 text-slateGray">
                      You haven&apos;t made any donations yet.
                    </div>
                  )
                ) : (
                  <>
                    <OrderLoader />
                    <OrderLoader />
                  </>
                )}
              </Grid>
            </TabPanel>

            {hasTickets && (
              <TabPanel>
                <Grid gridRowGap={32} paddingBottom={100}>
                  {ticketsData ? (
                    ticketsData.currentUserTickets.map(order => (
                      <TicketOrder key={order.id} order={order} />
                    ))
                  ) : (
                    <>
                      <OrderLoader />
                      <OrderLoader />
                    </>
                  )}
                </Grid>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </div>

      {ordersData && (
        <ModalTaxReport
          orders={ordersData.currentUserOrders}
          {...modalTaxReport}
        />
      )}
    </>
  )
}
